import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { RecipeCollection } from '@/types/api';

export type StoreStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface CollectionStore {
  collections: RecipeCollection[];
  status: StoreStatus;
  setCollections: (collections: RecipeCollection[]) => void;
  setStatus: (status: StoreStatus) => void;
  upsert: (collection: RecipeCollection) => void;
  remove: (id: string) => void;
  clear: () => void;
}

export const useCollectionStore = create<CollectionStore>()(
  persist(
    (set) => ({
      collections: [],
      status: 'idle' as StoreStatus,

      setCollections: (collections) => set({ collections }),

      setStatus: (status) => set({ status }),

      upsert: (collection) =>
        set((state) => {
          const idx = state.collections.findIndex((c) => c.id === collection.id);
          if (idx >= 0) {
            const existing = state.collections[idx]!;
            // Preserve richer detail fields if the incoming data doesn't include them.
            const merged: RecipeCollection = {
              ...existing,
              ...collection,
              recipeIds: collection.recipeIds ?? existing.recipeIds,
              ingredients: collection.ingredients ?? existing.ingredients,
            };
            return {
              collections: state.collections.map((c, i) => (i === idx ? merged : c)),
            };
          }
          return { collections: [collection, ...state.collections] };
        }),

      remove: (id) =>
        set((state) => ({ collections: state.collections.filter((c) => c.id !== id) })),

      clear: () => set({ collections: [], status: 'idle' }),
    }),
    {
      name: 'bp-collections',
      partialize: (state) => ({ collections: state.collections }),
    }
  )
);
